import { CircularProgress, TextField } from "@material-ui/core";
import { FormControlLabel } from "@material-ui/core";
import { Checkbox } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { Button } from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { User } from "../../models/User";
import {
	setPassword,
	setProfilesToUser,
	upsertUser,
} from "../../repositories/UsersRepository";
import { AuthTypeSelector } from "../common/AuthTypeSelector";
import { ProfilesPicker } from "../common/ProfilesPicker";

const useStyles = makeStyles((theme: Theme) => ({
	checkbox: {
		userSelect: "none",
	},
	title: {
		fontSize: 15,
	},
	chip: {
		margin: 2,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreateUser = ({ show, onHide }: Props) => {
	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [profileError, setprofileError] = useState("");

	const classes = useStyles();

	const {
		register,
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<User>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const UserName = watch("UserName");

	const onSubmit: SubmitHandler<User> = async (data: any) => {
		setIsSubmitLoading(true);
		const upsertUserResp = await upsertUser(data);
		if (upsertUserResp.ok) {
			const userId = upsertUserResp.data.tables[0].rows[0].UserId;
			const setPasswordResp = await setPassword(userId, data.UserName);
			const setProfilesToUserResp = await setProfilesToUser(
				userId,
				data.ProfilesId as number[],
			);
			if (setProfilesToUserResp.ok) {
				setIsSubmitLoading(false);
				setOpen(true);
				onHide(true);
			} else setError(setProfilesToUserResp.message);
		} else setError(upsertUserResp.message);
	};

	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);

	return (
		<>
			<MesfModal
				title="NEW USER"
				open={show}
				handleClose={() => onHide(false)}
				id="create-user-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						<Grid container spacing={1}>
							<Grid item xs={12} md={6}>
								<TextField
									{...register("UserName", { required: true })}
									label="Username"
									variant="outlined"
									error={!!errors.UserName}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
								{errors.UserName?.type === "required" && (
									<span className={classes.errorLabel}>
										User name is required
									</span>
								)}
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									disabled
									label="Password"
									value={UserName}
									variant="outlined"
									error={!!errors.UserName}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextField
									{...register("FirstName", { required: true })}
									label="First name"
									variant="outlined"
									error={!!errors.FirstName}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
								{errors.FirstName?.type === "required" && (
									<span className={classes.errorLabel}>
										First name is required
									</span>
								)}
							</Grid>
							<Grid item md={6} xs={12}>
								<TextField
									{...register("LastName", { required: true })}
									label="Last name"
									variant="outlined"
									error={!!errors.FirstName}
									fullWidth
									margin="dense"
									autoComplete="off"
								/>
								{errors.LastName?.type === "required" && (
									<span className={classes.errorLabel}>
										Last name is required
									</span>
								)}
							</Grid>
							{/* <Grid item container xs={12} spacing={1}> */}
							<Grid item xs={12} md={6}>
								<Controller
									name="ProfilesId"
									control={control}
									rules={{ required: true }}
									render={({ field: { onChange, value } }) => (
										<ProfilesPicker
											onChange={onChange}
											value={value as number[]}
											userIdForDelete={null}
											setprofileError={setprofileError}
										/>
									)}
								/>
								{errors.ProfilesId && (
									<span className={classes.errorLabel}>
										{"Select a profile(s)"}
									</span>
								)}
							</Grid>
							<Grid item xs={12} md={6}>
								<Controller
									name="AuthTypeId"
									control={control}
									render={({ field: { value, onChange } }) => (
										<AuthTypeSelector value={value} onChange={onChange} />
									)}
								/>
							</Grid>
							<Grid item xs={12} md={6} style={{ marginTop: "5px" }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											className={classes.checkbox}
											control={
												<Checkbox
													checked={field.value}
													onChange={field.onChange}
													name="IsActive"
													color="primary"
												/>
											}
											label="Active"
										/>
									)}
								/>
							</Grid>
						</Grid>
						{/* </Grid> */}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
				<ErrorModal
					error={error}
					onHide={() => setError("")}
					title="Error Creating User"
				/>
				<ErrorModal
					error={profileError}
					onHide={() => setprofileError("")}
					title="Error Choosing Profile"
				/>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="success" onClose={handleClose}>
					The user was created successfully
				</Alert>
			</Snackbar>
		</>
	);
};
